import { adminBffFetch } from '@avila-roffe/api-clients-admin-bff';
import { listAttachments } from '../skills/skillApi.js';
import type { Workflow } from './types.js';

// Re-export for consumers that import from workflowApi
export { listAttachments };

function toRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function normalizeWorkflow(raw: unknown): Workflow {
  const r = toRecord(raw);
  return {
    id: String(r.id ?? ''),
    appSlug: String(r.appSlug ?? ''),
    slug: String(r.slug ?? ''),
    name: String(r.name ?? r.slug ?? ''),
    displayName: typeof r.displayName === 'string' ? r.displayName : undefined,
    description: String(r.description ?? ''),
    version: typeof r.version === 'string' ? r.version : undefined,
    status: (['ACTIVE', 'DRAFT', 'DEPRECATED'].includes(r.status as string)
      ? r.status
      : 'DRAFT') as Workflow['status'],
    enabled: r.enabled !== false,
    triggerMode: (['MANUAL', 'SCHEDULED', 'WEBHOOK', 'EVENT'].includes(r.triggerMode as string)
      ? r.triggerMode
      : 'MANUAL') as Workflow['triggerMode'],
    scope: typeof r.scope === 'string' ? r.scope : undefined,
    tags: Array.isArray(r.tags) ? (r.tags as string[]) : [],
    content: String(r.rawContent ?? r.raw ?? r.content ?? ''),
    skillRefs: Array.isArray(r.skillRefs) ? (r.skillRefs as string[]) : [],
    toolRefs: Array.isArray(r.toolRefs) ? (r.toolRefs as string[]) : [],
    attachments: Array.isArray(r.attachments) ? (r.attachments as Workflow['attachments']) : [],
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : undefined,
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : undefined,
  };
}

export async function listWorkflows(limit = 50): Promise<Workflow[]> {
  const res = await adminBffFetch<{ data: { data?: unknown[] } }>(
    `/api/v1/workflows?limit=${limit}`,
    { method: 'GET' },
  );
  const raw = (res as { data: { data?: unknown[] } }).data?.data ?? [];
  return raw.map(normalizeWorkflow);
}

export async function createWorkflow(payload: {
  name: string;
  appSlug?: string;
  slug?: string;
  displayName?: string;
  description?: string;
  triggerMode?: string;
  scope?: string;
  status?: string;
  enabled?: boolean;
  tags?: string[];
  createdBy?: string;
}): Promise<Workflow> {
  const res = await adminBffFetch<{ data?: unknown }>('/api/v1/workflows', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return normalizeWorkflow((res as { data?: unknown }).data ?? res);
}

export async function updateWorkflow(
  id: string,
  payload: {
    name?: string;
    slug?: string;
    displayName?: string;
    description?: string;
    enabled?: boolean;
    triggerMode?: string;
    scope?: string;
    status?: string;
    tags?: string[];
    skillRefs?: string[];
    toolRefs?: string[];
    updatedBy?: string;
  },
): Promise<Workflow> {
  const res = await adminBffFetch<{ data?: unknown }>(`/api/v1/workflows/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return normalizeWorkflow((res as { data?: unknown }).data ?? res);
}

export async function deleteWorkflow(id: string): Promise<void> {
  await adminBffFetch(`/api/v1/workflows/${id}`, { method: 'DELETE' });
}

export async function getWorkflowById(id: string): Promise<Workflow> {
  const res = await adminBffFetch<{ data?: unknown }>(`/api/v1/workflows/${id}`, {
    method: 'GET',
  });
  const envelope = res as { data?: unknown };
  const raw = envelope.data ?? res;
  return normalizeWorkflow(raw);
}
